import {
  Box,
  IconButton,
  Typography,
  useTheme,
  Avatar,
  Popper,
  Paper,
  Tooltip,
  Divider,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Drawer,
  useMediaQuery,
} from "@mui/material";
import MenuIcon from "@mui/icons-material/Menu";
import CloseIcon from "@mui/icons-material/Close";
import GlobalSearchBar from "../components/common/GlobalSearchBar";
import {
  DarkModeOutlined,
  HomeOutlined,
  LightModeOutlined,
  LogoutOutlined,
  NewReleases,
  SettingsOutlined,
  SupportOutlined,
} from "@mui/icons-material";
import { useContext, useRef, useState, useEffect, useMemo } from "react";
import { ColorModeContext, tokens } from "../theme";
import { useNavigate } from "react-router-dom";
import { useSelector } from "react-redux";
import type { RootState } from "../store/Index";
import { logout, type User } from "../store/slice/AuthSlice";
import Breadcrumbs from "../components/common/BreadCrumbs";
import NotificationBellWithPopover from "./Notifications";
import GridViewOutlinedIcon from "@mui/icons-material/GridViewOutlined";
import ToolsAssetsDrawer from "./ToolsAssetsDrawer";
import { fetchUsers } from "../store/slice/user-management/usersThunks";
import { useAppDispatch } from "../store/hooks";
import AppLauncher, {
  CUSTOM_ICON_MAP,
} from "./components/app-launcher/AppLauncher";
import {
  AppItem,
  apps as defaultApps,
  useCustomApps,
} from "./components/app-launcher/App";

const STORAGE_KEY_ORDER = "appOrder";

const TopBar = () => {
  const theme = useTheme();
  const reduxTheme = useSelector((state: RootState) => state.theme);
  const colors = tokens(theme.palette.mode, reduxTheme);
  const colorMode = useContext(ColorModeContext);
  const avatarRef = useRef<HTMLDivElement | null>(null);
  const [anchorEl, setAnchorEl] = useState<HTMLDivElement | null>(null);
  const [openPopper, setOpenPopper] = useState(false);
  const [openToolsDrawer, setOpenToolsDrawer] = useState(false);
  const [openMobileMenu, setOpenMobileMenu] = useState(false);

  const [appsAnchorEl, setAppsAnchorEl] = useState<HTMLElement | null>(null);
  const closeAppsTimeoutRef = useRef<NodeJS.Timeout | null>(null);

  const clearAppsTimeout = () => {
    if (closeAppsTimeoutRef.current) {
      clearTimeout(closeAppsTimeoutRef.current);
      closeAppsTimeoutRef.current = null;
    }
  };

  const scheduleCloseApps = () => {
    clearAppsTimeout();
    closeAppsTimeoutRef.current = setTimeout(() => {
      setAppsAnchorEl(null);
    }, 800);
  };

  const handleOpenApps = (event: React.MouseEvent<HTMLElement>) => {
    setAppsAnchorEl(event.currentTarget);
  };

  const handleCloseApps = () => {
    setAppsAnchorEl(null);
  };

  const appsOpen = Boolean(appsAnchorEl);
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));

  const dispatch = useAppDispatch();
  const user = useSelector(
    (state: RootState) => state.auth.user,
  ) as User | null;
  const users = useSelector((state: RootState) => state.users.users);
  const userDetails = users.find((u) => u.id === user?.id);

  const navigate = useNavigate();
  const closePopperTimeoutRef = useRef<NodeJS.Timeout | null>(null);

  useEffect(() => {
    dispatch(fetchUsers());
  }, [dispatch, user]);

  const { customApps, addCustomApp, removeCustomApp } = useCustomApps();

  const [appOrder, setAppOrder] = useState<string[]>(() => {
    try {
      const saved = localStorage.getItem(STORAGE_KEY_ORDER);
      return saved ? (JSON.parse(saved) as string[]) : [];
    } catch {
      return [];
    }
  });

  const mergedApps = useMemo<AppItem[]>(() => {
    const customAsAppItems: AppItem[] = customApps.map((stored) => ({
      label: stored.label,
      route: stored.route,
      color: stored.color,
      icon: CUSTOM_ICON_MAP[stored.iconKey] ?? CUSTOM_ICON_MAP["OpenInNew"],
      isCustom: true,
    }));

    const allApps = [...defaultApps, ...customAsAppItems];

    if (appOrder.length === 0) return allApps;

    const reordered = appOrder
      .map((label) => allApps.find((app) => app.label === label))
      .filter((app): app is AppItem => app !== undefined);

    const reorderedLabels = new Set(appOrder);
    const unseen = allApps.filter((app) => !reorderedLabels.has(app.label));

    return [...reordered, ...unseen];
  }, [appOrder, customApps]);

  useEffect(() => {
    const handleStorage = (event: StorageEvent) => {
      if (event.key !== "appOrder") return;

      try {
        setAppOrder(event.newValue ? JSON.parse(event.newValue) : []);
      } catch (error) {
        console.error("Failed to sync app order from localStorage", error);
      }
    };

    window.addEventListener("storage", handleStorage);

    return () => {
      window.removeEventListener("storage", handleStorage);
    };
  }, []);

  const handleReorder = (newOrder: AppItem[]) => {
    const labels = newOrder.map((app) => app.label);
    setAppOrder(labels);
    localStorage.setItem(STORAGE_KEY_ORDER, JSON.stringify(labels));
  };

  const handleRemoveCustomApp = (label: string) => {
    removeCustomApp(label);
    setAppOrder((prev) => {
      const next = prev.filter((l) => l !== label);
      localStorage.setItem(STORAGE_KEY_ORDER, JSON.stringify(next));
      return next;
    });
  };

  const clearTimeoutRef = () => {
    if (closePopperTimeoutRef.current !== null) {
      clearTimeout(closePopperTimeoutRef.current);
      closePopperTimeoutRef.current = null;
    }
  };

  const handleOpenPopper = () => {
    clearTimeoutRef();
    setAnchorEl(avatarRef.current);
    setOpenPopper(true);
  };

  const handleClosePopper = () => {
    clearTimeoutRef();
    closePopperTimeoutRef.current = setTimeout(() => setOpenPopper(false), 200);
  };

  const handleOpenTools = () => setOpenToolsDrawer(true);
  const handleCloseTools = () => setOpenToolsDrawer(false);
  const handleOpenMobileMenu = () => setOpenMobileMenu(true);
  const handleCloseMobileMenu = () => setOpenMobileMenu(false);

  const handleWhatsNew = () => {
    setOpenPopper(false);
    navigate("/changelog");
  };
  const handleLogout = () => {
    dispatch(logout());
    setOpenPopper(false);
    navigate("/login");
  };
  const handleSettings = () => {
    navigate("/settings");
    setOpenPopper(false);
  };
  const handleSupport = () => {
    window.open("https://muzukuru.com/tickets", "_blank");
  };

  if (!user) return null;

  const avatarInitial = user.username?.charAt(0)?.toUpperCase() || "?";

  // const borderColor =
  //   theme.palette.mode === "dark"
  //     ? "rgba(255,255,255,0.15)"
  //     : "rgba(0,0,0,0.15)";

  return (
    <Box
      className="topbar"
      width="100%"
      sx={{
        borderRadius: "8px",
        position: "sticky",
        backgroundColor: colors.primary[800],
        pl: { xs: 0, md: 2 },
        top: 0,
        zIndex: 10,
      }}
    >
      <Box
        display="flex"
        justifyContent="space-between"
        alignItems="center"
        p={2}
        sx={{ backgroundColor: colors.primary[800], gap: { xs: 1, md: 2 } }}
      >
        {/* Left: Mobile Menu or Breadcrumbs */}
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            minWidth: 0,
            flex: { xs: "0 0 auto", md: "1 1 auto" },
          }}
        >
          {isMobile ? (
            <IconButton
              onClick={handleOpenMobileMenu}
              sx={{ p: 0.5, backgroundColor: "transparent" }}
              aria-label="menu"
            >
              <MenuIcon />
            </IconButton>
          ) : (
            <Breadcrumbs />
          )}
        </Box>

        {!isMobile && (
          <Box
            sx={{
              flex: 1,
              display: "flex",
              justifyContent: "center",
              gap: 2,
              alignItems: "center",
              maxWidth: "400px",
            }}
          >
            
              <IconButton
                onClick={() => navigate("/app-home")}
                sx={{
                  backgroundColor: colors.primary[900] + "50",
                  //border: `2px solid ${borderColor}`,
                  borderRadius: "50px",
                  width: 48,
                  height: 48,
                  flexShrink: 0,
                  "&:hover": { backgroundColor: colors.primary[900] + "80" },
                }}
              >
                <HomeOutlined fontSize="large" />
              </IconButton>
            
            <GlobalSearchBar size="small" />
          </Box>
        )}

        {/* Right: Actions */}
        <Box
          sx={{
            flex: 1,
            display: "flex",
            alignItems: "center",
            gap: { xs: 0.5, md: 1.5 },
            justifyContent: "flex-end",
          }}
        >
          {isMobile && <GlobalSearchBar compact />}

          {!isMobile && (
            <>
              <Tooltip title="Apps" arrow>
                <IconButton aria-label="Apps" onClick={handleOpenApps}>
                  <GridViewOutlinedIcon />
                </IconButton>
              </Tooltip>

              <NotificationBellWithPopover />

              <Tooltip
                title={
                  theme.palette.mode === "dark" ? "Light Mode" : "Dark Mode"
                }
                arrow
              >
                <IconButton
                  aria-label="Toggle theme"
                  onClick={colorMode.toggleColorMode}
                >
                  {theme.palette.mode === "dark" ? (
                    <LightModeOutlined />
                  ) : (
                    <DarkModeOutlined />
                  )}
                </IconButton>
              </Tooltip>
            </>
          )}

          <Box
            ref={avatarRef}
            onMouseEnter={!isMobile ? handleOpenPopper : undefined}
            onMouseLeave={!isMobile ? handleClosePopper : undefined}
            onClick={isMobile ? handleOpenPopper : undefined}
            onKeyDown={(e) => e.key === "Enter" && handleOpenPopper()}
            tabIndex={0}
            sx={{
              cursor: "pointer",
              display: "flex",
              alignItems: "center",
              border: `1px solid ${colors.primary[300]}`,
              borderRadius: "8px",
              p: 0.5,
            }}
            role="button"
            aria-label="User profile"
          >
            <Avatar
              sx={{ width: 32, height: 32 }}
              src={userDetails?.profile_picture_url || undefined}
            >
              {avatarInitial}
            </Avatar>
            {!isMobile && (
              <Box sx={{ ml: 1, textAlign: "left" }}>
                <Typography variant="subtitle2">
                  {user?.first_name && user?.last_name
                    ? `${user.first_name} ${user.last_name}`
                    : user?.username || "Guest"}
                </Typography>
              </Box>
            )}
          </Box>

          <Popper
            open={openPopper}
            anchorEl={anchorEl}
            placement="bottom-end"
            sx={{ zIndex: 11 }}
            modifiers={[{ name: "offset", options: { offset: [0, 8] } }]}
          >
            <Paper
              onMouseEnter={!isMobile ? handleOpenPopper : undefined}
              onMouseLeave={!isMobile ? handleClosePopper : undefined}
              elevation={3}
              sx={{ width: 250, borderRadius: theme.shape.borderRadius }}
            >
              <Box
                sx={{
                  p: 2,
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "center",
                  gap: 1,
                }}
              >
                <Avatar
                  sx={{ width: 32, height: 32 }}
                  src={userDetails?.profile_picture_url || undefined}
                >
                  {avatarInitial}
                </Avatar>
                <Typography variant="subtitle1" fontWeight="bold">
                  {user.first_name} {user.last_name}
                </Typography>
              </Box>
              <Divider />
              <ListItemButton onClick={handleWhatsNew} aria-label="What's New">
                <ListItemIcon>
                  <NewReleases />
                </ListItemIcon>
                <ListItemText primary="What's New" />
              </ListItemButton>
              <ListItemButton onClick={handleSettings} aria-label="Settings">
                <ListItemIcon>
                  <SettingsOutlined />
                </ListItemIcon>
                <ListItemText primary="Settings" />
              </ListItemButton>
              <ListItemButton onClick={handleSupport} aria-label="Support">
                <ListItemIcon>
                  <SupportOutlined />
                </ListItemIcon>
                <ListItemText primary="Support" />
              </ListItemButton>
              <ListItemButton onClick={handleLogout} aria-label="Logout">
                <ListItemIcon>
                  <LogoutOutlined />
                </ListItemIcon>
                <ListItemText primary="Logout" />
              </ListItemButton>
            </Paper>
          </Popper>
        </Box>
      </Box>

      <Drawer
        anchor="left"
        open={openMobileMenu}
        onClose={handleCloseMobileMenu}
      >
        <Box
          sx={{
            width: 280,
            p: 2,
            display: "flex",
            flexDirection: "column",
            height: "100%",
            backgroundColor: colors.primary[800],
          }}
        >
          <Box
            display="flex"
            justifyContent="space-between"
            alignItems="center"
            mb={2}
          >
            <Typography variant="h6" fontWeight="bold">
              Menu
            </Typography>
            <IconButton
              onClick={handleCloseMobileMenu}
              aria-label="close menu"
              size="small"
            >
              <CloseIcon />
            </IconButton>
          </Box>
          <Divider />
          <Box sx={{ mt: 2, display: "flex", flexDirection: "column", gap: 1 }}>
            <ListItemButton
              onClick={() => {
                handleOpenTools();
                handleCloseMobileMenu();
              }}
            >
              <ListItemIcon>
                <GridViewOutlinedIcon />
              </ListItemIcon>
              <ListItemText primary="Tool Assets" />
            </ListItemButton>
            <ListItemButton onClick={colorMode.toggleColorMode}>
              <ListItemIcon>
                {theme.palette.mode === "dark" ? (
                  <LightModeOutlined />
                ) : (
                  <DarkModeOutlined />
                )}
              </ListItemIcon>
              <ListItemText primary="Toggle Theme" />
            </ListItemButton>
            <ListItemButton
              onClick={() => {
                navigate("/changelog");
                handleCloseMobileMenu();
              }}
            >
              <ListItemIcon>
                <NewReleases />
              </ListItemIcon>
              <ListItemText primary="What's New" />
            </ListItemButton>
            <ListItemButton
              onClick={() => {
                navigate("/settings");
                handleCloseMobileMenu();
              }}
            >
              <ListItemIcon>
                <SettingsOutlined />
              </ListItemIcon>
              <ListItemText primary="Settings" />
            </ListItemButton>
            <ListItemButton onClick={handleSupport}>
              <ListItemIcon>
                <SupportOutlined />
              </ListItemIcon>
              <ListItemText primary="Support" />
            </ListItemButton>
            <Divider sx={{ my: 1 }} />
            <ListItemButton onClick={handleLogout}>
              <ListItemIcon>
                <LogoutOutlined />
              </ListItemIcon>
              <ListItemText primary="Logout" />
            </ListItemButton>
          </Box>
        </Box>
      </Drawer>

      <ToolsAssetsDrawer open={openToolsDrawer} onClose={handleCloseTools} />

      <AppLauncher
        anchorEl={appsAnchorEl}
        open={appsOpen}
        onClose={handleCloseApps}
        onMouseEnter={clearAppsTimeout}
        onMouseLeave={scheduleCloseApps}
        onOpenToolsDrawer={handleOpenTools}
        appList={mergedApps}
        onReorder={handleReorder}
        onAddCustomApp={addCustomApp}
        onRemoveCustomApp={handleRemoveCustomApp}
      />
    </Box>
  );
};

export default TopBar;
